import { makeClassName, pluralize } from '@siteless/util';
import { useMemo } from 'react';
import ballotDisplayOrder from '../resources/Ballot/constants/ballotDisplayOrder';
import optionTypes from '../resources/Ballot/constants/optionTypes';
import ObjectiveStateResource, {
  ObjectiveStateResourceType,
} from '../resources/ObjectiveState';
import { getScoreMap } from '../util/getScoreMap';
import useCopy from '../util/useCopy';
import EditIcon from './EditIcon';

export let BallotPreviewItem = ({
  ballotItemDisplayName,
  selectedOption,
  onClick,
  className,
  isEditable,
  showScore,
}: {
  ballotItemDisplayName: string;
  selectedOption: ObjectiveStateResourceType['ballotShape'][(typeof ballotDisplayOrder)[number]]['options'][string];
  onClick?: () => void;
  className?: string;
  isEditable?: boolean;
  showScore?: boolean;
}) => {
  let copy = useCopy();
  let objectiveState = ObjectiveStateResource.use();
  let type = optionTypes[selectedOption.type];

  let optionComponentProps = useMemo(() => {
    if (type.key === 'winner' && selectedOption.type === 'winner')
      return type.getOptionComponentProps({
        data: selectedOption.data,
        copy,
        objectiveState,
      });
    if (type.key === 'eventBoolean' && selectedOption.type === 'eventBoolean')
      return type.getOptionComponentProps({
        data: selectedOption.data,
        copy,
      });
    if (type.key === 'eventCount' && selectedOption.type === 'eventCount')
      return type.getOptionComponentProps({
        data: selectedOption.data,
        copy,
      });
    if (type.key === 'pass' && selectedOption.type === 'pass')
      return type.getOptionComponentProps({
        data: selectedOption.data,
        copy,
      });
  }, [copy, objectiveState, selectedOption, type]);

  let score = useMemo(() => {
    if (type.key === 'winner' && selectedOption.type === 'winner') {
      return getScoreMap.winner({
        results: objectiveState.results,
        data: selectedOption.data,
      });
    }
    if (type.key === 'eventBoolean' && selectedOption.type === 'eventBoolean')
      return getScoreMap.eventBoolean({
        events: objectiveState.events,
        data: selectedOption.data,
      });
    if (type.key === 'eventCount' && selectedOption.type === 'eventCount')
      return getScoreMap.eventCount({
        events: objectiveState.events,
        data: selectedOption.data,
      });
    if (type.key === 'pass' && selectedOption.type === 'pass')
      return getScoreMap.pass({
        results: objectiveState.results,
        data: selectedOption.data,
      });
  }, [objectiveState, selectedOption, type]);

  if (optionComponentProps === undefined) return null;

  let { title, posterImageUrl } = optionComponentProps;
  let showPointsIndicator = showScore && typeof score === 'number' && score > 0;
  let isLoss = showScore && score === 0;

  let graphic = posterImageUrl ? (
    <img
      src={posterImageUrl}
      className={makeClassName('h-full w-full object-cover', isLoss && 'grayscale opacity-80')}
      alt={title}
    />
  ) : (
    <div className="bg-neutral-0 flex h-full w-full items-center justify-center">
      <type.Icon />
    </div>
  );

  return (
    <div
      className={makeClassName(
        'mb-4 flex text-left text-neutral-900 last:mb-0',
        className,
      )}
      onClick={onClick}
    >
      <div
        className={makeClassName(
          'mr-3 h-14 w-14 flex-none overflow-hidden rounded-full border-4',
          showPointsIndicator ? 'border-rose-default' : 'border-neutral-100',
        )}
      >
        <div className="border-neutral-0 h-full w-full overflow-hidden rounded-full border-2">
          {graphic}
        </div>
      </div>
      <div
        className={makeClassName(
          'flex-1 pt-1',
          isLoss && 'text-neutral-400',
        )}
      >
        <div className={makeClassName('flex justify-between text-sm font-medium', isLoss && 'text-neutral-500')}>
          {ballotItemDisplayName}
          {isEditable && <EditIcon className="text-rose-bright h-4 w-4" />}
        </div>
        <div className={isLoss ? 'line-through' : undefined}>{title}</div>
        {showPointsIndicator && typeof score === 'number' && (
          <div className="text-rose-default text-sm font-medium">
            +{' '}
            {pluralize({
              count: score,
              singular: copy.point,
            })}
          </div>
        )}
      </div>
    </div>
  );
};
